import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Cookie, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { applyStoredConsent, readConsent, setConsent } from "@/lib/analytics";

/**
 * A short, honest cookie notice.
 *
 * The Meta pixel and the Google Ads tag in index.html both start with consent
 * withheld, so nothing is measured until somebody presses Accept here. A choice
 * is remembered, so the notice is only ever seen once, and staff screens never
 * see it at all.
 */
export const CookieNotice = () => {
  const { pathname } = useLocation();
  const staffScreen = pathname.startsWith("/admin");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    applyStoredConsent();
    if (!staffScreen && !readConsent()) {
      // A small delay keeps it from competing with the page loading in.
      const timer = window.setTimeout(() => setVisible(true), 900);
      return () => window.clearTimeout(timer);
    }
    setVisible(false);
    return undefined;
  }, [staffScreen]);

  if (!visible) return null;

  const choose = (granted) => {
    setConsent(granted);
    setVisible(false);
  };

  return (
    <div
      role="dialog"
      aria-label="Cookie notice"
      data-testid="cookie-notice"
      className="tt-fade-in fixed inset-x-0 bottom-0 z-[300] px-3 pb-3 sm:px-5 sm:pb-5"
    >
      <div className="mx-auto max-w-3xl rounded-xl border border-border bg-white/95 p-4 shadow-lg backdrop-blur-md sm:p-5">
        <div className="flex items-start gap-3">
          <span
            aria-hidden="true"
            className="mt-0.5 hidden h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[color:var(--tt-teal-mist)] text-[color:var(--tt-teal-deep)] sm:flex"
          >
            <Cookie className="h-4.5 w-4.5" />
          </span>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-foreground">
              We&apos;d like to measure how people find this calculator
            </p>
            <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
              With your permission we use a Meta (Facebook) pixel and a Google Ads tag
              to count visits and completed quotes, so we know which adverts are worth
              the money. It is only ever used for that. Decline and nothing is measured
              — the calculator works exactly the same either way.
            </p>

            <div className="mt-3.5 flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => choose(true)}
                data-testid="cookie-notice-accept-button"
                className="h-10"
              >
                Accept
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => choose(false)}
                data-testid="cookie-notice-decline-button"
                className="h-10 bg-white"
              >
                No thanks
              </Button>
            </div>
          </div>

          <button
            type="button"
            onClick={() => choose(false)}
            aria-label="Close and decline"
            data-testid="cookie-notice-close-button"
            className="-mr-1 -mt-1 rounded-md p-1.5 text-muted-foreground transition-colors duration-150 hover:bg-[color:var(--tt-surface-2)] hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--tt-teal)]"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  );
};
